import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  CheckCircle2,
  Clock3,
  Cloud,
  Database,
  LogOut,
  RefreshCw,
  ShieldCheck,
  Wifi,
  WifiOff,
  XCircle,
} from 'lucide-react';
import {
  getCurrentProfile,
  getCurrentSession,
  isManagementProfile,
  signIn,
  signOut,
  type Profile,
} from '../services/auth/authService';
import {
  loadDashboard,
  type AuditLogRow,
  type DashboardKpis,
  type DashboardRange,
  type SyncHealthRow,
} from '../services/ownerDashboard';
import { CatalogManagement } from '../components/owner/CatalogManagement';
import { requestSync } from '../services/sync';

const money = (centavos: number) =>
  `₱${(Number(centavos || 0) / 100).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dateInput = (d: Date) => {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};
const rangeFor = (preset: string, from = '', to = ''): DashboardRange => {
  const now = new Date();
  const end = new Date(now);
  end.setDate(end.getDate() + 1);
  end.setHours(0, 0, 0, 0);
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  if (preset === 'yesterday') {
    start.setDate(start.getDate() - 1);
    end.setDate(end.getDate() - 1);
  } else if (preset === 'week') {
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  } else if (preset === 'month') {
    start.setDate(1);
  } else if (preset === 'custom' && from && to) {
    const f = new Date(`${from}T00:00:00`);
    const t = new Date(`${to}T00:00:00`);
    t.setDate(t.getDate() + 1);
    return { from: f.toISOString(), to: t.toISOString() };
  }
  return { from: start.toISOString(), to: end.toISOString() };
};
const initialKpis: DashboardKpis = {
  sales_centavos: 0,
  order_count: 0,
  kg_processed: 0,
  cash_centavos: 0,
  gcash_centavos: 0,
  maya_centavos: 0,
  pending_orders: 0,
  unclaimed_orders: 0,
};
const dashboardPresets = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'custom', label: 'Custom Range' },
] as const;

function Login({ onLogin }: { onLogin: (profile: Profile) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await signIn(email, password);
      const profile = await getCurrentProfile();
      if (!isManagementProfile(profile)) {
        await signOut();
        throw new Error('Owner or manager access is required.');
      }
      onLogin(profile);
      requestSync();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to sign in.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grid min-h-[calc(100vh-78px)] place-items-center bg-[#F7FAFC] p-6">
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 shadow-sm"
      >
        <div className="mb-7">
          <div className="mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-[#DCEEFF] text-[#00549C]">
            <ShieldCheck size={28} />
          </div>
          <p className="text-xs font-black uppercase tracking-[0.2em] text-[#00549C]">
            BABS MANAGEMENT
          </p>
          <h1 className="mt-1 text-3xl font-black text-[#003B70]">Owner Dashboard</h1>
          <p className="mt-2 text-sm font-medium text-slate-500">
            Secure reporting access for authorized owners and managers.
          </p>
        </div>
        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700">
            {error}
          </div>
        )}
        <label className="block text-sm font-bold text-slate-700">
          Email
          <input
            required
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-2 min-h-12 w-full rounded-xl border border-slate-200 px-4 outline-none focus:border-[#00549C]"
          />
        </label>
        <label className="mt-4 block text-sm font-bold text-slate-700">
          Password
          <input
            required
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2 min-h-12 w-full rounded-xl border border-slate-200 px-4 outline-none focus:border-[#00549C]"
          />
        </label>
        <button
          disabled={busy}
          className="mt-6 min-h-14 w-full rounded-xl bg-[#00549C] px-5 text-base font-black text-white disabled:opacity-50"
        >
          {busy ? 'SIGNING IN…' : 'SIGN IN'}
        </button>
      </form>
    </div>
  );
}

function Kpi({
  label,
  value,
  detail,
  icon: Icon,
}: {
  label: string;
  value: string;
  detail?: string;
  icon: typeof BarChart3;
}) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-black uppercase tracking-widest text-slate-400">{label}</p>
          <p className="mt-2 text-3xl font-black tracking-tight text-[#003B70]">{value}</p>
          {detail && <p className="mt-1 text-xs font-semibold text-slate-500">{detail}</p>}
        </div>
        <div className="grid h-11 w-11 place-items-center rounded-xl bg-[#DCEEFF] text-[#00549C]">
          <Icon size={21} />
        </div>
      </div>
    </section>
  );
}

export function OwnerDashboardPage() {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [preset, setPreset] = useState('today');
  const [from, setFrom] = useState(dateInput(new Date()));
  const [to, setTo] = useState(dateInput(new Date()));
  const [data, setData] = useState<Awaited<ReturnType<typeof loadDashboard>> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastLoaded, setLastLoaded] = useState<Date | null>(null);
  const range = useMemo(() => rangeFor(preset, from, to), [preset, from, to]);
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await loadDashboard(range));
      setLastLoaded(new Date());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to load dashboard.');
    } finally {
      setLoading(false);
    }
  }, [range]);
  useEffect(() => {
    void (async () => {
      try {
        const session = await getCurrentSession();
        if (session) {
          const p = await getCurrentProfile();
          if (isManagementProfile(p)) setProfile(p);
        }
      } finally {
        setAuthChecked(true);
      }
    })();
  }, []);
  useEffect(() => {
    if (profile) void load();
  }, [profile, load]);
  if (!authChecked)
    return (
      <div className="grid min-h-[calc(100vh-78px)] place-items-center text-sm font-bold text-slate-500">
        Checking secure access…
      </div>
    );
  if (!profile) return <Login onLogin={setProfile} />;
  const k = data?.kpis ?? initialKpis;
  return (
    <div className="min-h-[calc(100vh-78px)] bg-[#F7FAFC] p-4 lg:p-6">
      <div className="mx-auto max-w-[1366px] space-y-5">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#00549C]">
              BABS LAUNDRY SHOP • MANAGEMENT
            </p>
            <h1 className="mt-1 text-3xl font-black tracking-tight text-[#003B70]">
              {catalogOpen ? 'Service Catalog' : 'Owner Dashboard'}
            </h1>
            <p className="mt-1 text-sm font-medium text-slate-500">
              {catalogOpen
                ? 'Manage services, prices, categories and add-ons'
                : 'Operational and financial reporting'}{' '}
              • {profile.full_name}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/pos"
              className="flex min-h-11 items-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-black text-[#00549C]"
            >
              Open POS
            </Link>
            <button
              onClick={() => setCatalogOpen((open) => !open)}
              className={`flex min-h-11 items-center gap-2 rounded-xl px-4 text-sm font-black ${catalogOpen ? 'bg-[#DCEEFF] text-[#00549C]' : 'border border-[#9EC6E9] bg-white text-[#00549C]'}`}
            >
              {catalogOpen ? 'Back to reports' : 'Manage catalog'}
            </button>
            <button
              onClick={() => void load()}
              className="flex min-h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-black text-slate-700"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
            <button
              onClick={() => void signOut().then(() => setProfile(null))}
              className="flex min-h-11 items-center gap-2 rounded-xl bg-[#003B70] px-4 text-sm font-black text-white"
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </header>
        {catalogOpen ? (
          <CatalogManagement branchId={profile.branch_id} />
        ) : (
          <>
            <section className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="flex flex-wrap gap-2">
                {dashboardPresets.map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => setPreset(value)}
                    className={`min-h-10 rounded-lg px-4 text-sm font-black ${preset === value ? 'bg-[#00549C] text-white' : 'bg-slate-50 text-slate-600'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {preset === 'custom' && (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="date"
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                    className="min-h-10 rounded-lg border border-slate-200 px-3 text-sm font-bold"
                  />
                  <span className="text-slate-400">to</span>
                  <input
                    type="date"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    className="min-h-10 rounded-lg border border-slate-200 px-3 text-sm font-bold"
                  />
                </div>
              )}
              <span className="ml-auto text-xs font-semibold text-slate-400">
                {lastLoaded ? `Updated ${lastLoaded.toLocaleTimeString('en-PH')}` : 'Loading…'}
              </span>
            </section>
            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
                {error}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
              <Kpi
                label="Today's Sales"
                value={money(k.sales_centavos)}
                detail={`${k.order_count} completed orders`}
                icon={BarChart3}
              />
              <Kpi
                label="KG Processed"
                value={`${Number(k.kg_processed || 0).toLocaleString('en-PH', { maximumFractionDigits: 3 })} KG`}
                icon={Database}
              />
              <Kpi label="Cash" value={money(k.cash_centavos)} icon={BarChart3} />
              <Kpi label="GCash" value={money(k.gcash_centavos)} icon={Cloud} />
              <Kpi label="Maya" value={money(k.maya_centavos)} icon={Cloud} />
              <Kpi label="Orders" value={String(k.order_count)} icon={Database} />
              <Kpi label="Pending Orders" value={String(k.pending_orders)} icon={Clock3} />
              <Kpi label="Unclaimed Orders" value={String(k.unclaimed_orders)} icon={Clock3} />
            </div>
            <div className="grid gap-5 xl:grid-cols-2">
              <ReportTable
                title="Daily Sales"
                headers={['Day', 'Orders', 'Sales']}
                rows={(data?.dailySales ?? []).map((r) => [
                  r.day,
                  r.order_count,
                  money(r.sales_centavos),
                ])}
              />
              <ReportTable
                title="Payment Breakdown"
                headers={['Method', 'Count', 'Amount']}
                rows={(data?.payments ?? []).map((r) => [
                  r.method,
                  r.payment_count,
                  money(r.amount_centavos),
                ])}
              />
              <ReportTable
                title="Services"
                headers={['Service', 'Orders', 'KG', 'Sales']}
                rows={(data?.services ?? []).map((r) => [
                  r.service_name,
                  r.order_count,
                  `${Number(r.kg_volume || 0).toLocaleString('en-PH', { maximumFractionDigits: 3 })}`,
                  money(r.sales_centavos),
                ])}
              />
              <ReportTable
                title="KG Volume"
                headers={['Day', 'KG']}
                rows={(data?.kgVolume ?? []).map((r) => [
                  r.day,
                  Number(r.kg_volume || 0).toLocaleString('en-PH', { maximumFractionDigits: 3 }),
                ])}
              />
              <ReportTable
                title="Cashier Activity"
                headers={['Cashier', 'Orders', 'Sales', 'Claimed']}
                rows={(data?.cashierActivity ?? []).map((r) => [
                  r.cashier_name,
                  r.order_count,
                  money(r.sales_centavos),
                  r.claimed_count,
                ])}
              />
              <ReportTable
                title="Pending Orders"
                headers={['Order', 'Status', 'Payment', 'Total']}
                rows={(data?.pendingOrders ?? [])
                  .slice(0, 20)
                  .map((r) => [
                    r.order_number,
                    r.order_status,
                    r.payment_status,
                    money(r.total_centavos),
                  ])}
              />
              <ReportTable
                title="Voided Orders"
                headers={['Order', 'Cashier', 'Amount', 'Reason']}
                rows={(data?.voidedOrders ?? [])
                  .slice(0, 20)
                  .map((r) => [
                    r.order_number,
                    r.cashier_name,
                    money(r.total_centavos),
                    r.reason || '—',
                  ])}
              />
              <AuditHistory rows={data?.auditLogs ?? []} />
              <SyncHealth rows={data?.syncHealth ?? []} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ReportTable({
  title,
  headers,
  rows,
}: {
  title: string;
  headers: string[];
  rows: ReactNode[][];
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
        <h2 className="font-black text-[#003B70]">{title}</h2>
        <span className="text-xs font-bold text-slate-400">{rows.length} rows</span>
      </div>
      <div className="max-h-72 overflow-auto">
        <table className="w-full text-left text-sm">
          <thead className="sticky top-0 bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-400">
            <tr>
              {headers.map((h) => (
                <th key={h} className="px-5 py-3">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={headers.length}
                  className="px-5 py-8 text-center font-semibold text-slate-400"
                >
                  No data for this period.
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  {r.map((c, j) => (
                    <td
                      key={j}
                      className={`px-5 py-3 ${j === 0 ? 'font-bold text-slate-700' : 'font-semibold text-slate-500'}`}
                    >
                      {c}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
function AuditHistory({ rows }: { rows: AuditLogRow[] }) {
  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm xl:col-span-2">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
        <div>
          <h2 className="font-black text-[#003B70]">Audit History</h2>
          <p className="mt-1 text-xs font-semibold text-slate-400">
            Authorized management view • customer details are masked from audit metadata
          </p>
        </div>
        <ShieldCheck size={18} className="text-[#00549C]" />
      </div>
      <div className="max-h-96 overflow-auto">
        <table className="w-full text-left text-sm">
          <thead className="sticky top-0 bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-400">
            <tr>
              {['Time', 'Actor', 'Action', 'Entity', 'Details'].map((h) => (
                <th key={h} className="px-5 py-3">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-5 py-8 text-center font-semibold text-slate-400">
                  No audit events for this period.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.audit_id} className="border-t border-slate-100">
                  <td className="px-5 py-3 whitespace-nowrap font-semibold text-slate-500">
                    {new Date(r.created_at).toLocaleString('en-PH')}
                  </td>
                  <td className="px-5 py-3 font-bold text-slate-700">
                    {r.actor_name}
                    {r.actor_role ? (
                      <span className="ml-2 rounded-full bg-slate-50 px-2 py-1 text-[10px] font-black uppercase text-slate-400">
                        {r.actor_role}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-5 py-3 font-black text-[#00549C]">{r.action}</td>
                  <td className="px-5 py-3 font-semibold text-slate-500">{r.entity_type}</td>
                  <td className="max-w-[420px] px-5 py-3 font-mono text-xs text-slate-500">
                    {JSON.stringify(r.metadata ?? {})}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
function SyncHealth({ rows }: { rows: SyncHealthRow[] }) {
  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
        <div>
          <h2 className="font-black text-[#003B70]">Sync Health</h2>
          <p className="mt-1 text-xs font-semibold text-slate-400">
            Device-level synchronization telemetry
          </p>
        </div>
        <Wifi size={18} className="text-[#00549C]" />
      </div>
      <div className="divide-y divide-slate-100">
        {rows.length === 0 ? (
          <p className="p-6 text-center font-semibold text-slate-400">
            No active devices reported.
          </p>
        ) : (
          rows.map((r) => (
            <div key={r.device_id} className="flex flex-wrap items-center gap-4 px-5 py-4">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#DCEEFF] text-[#00549C]">
                <Wifi size={18} />
              </div>
              <div className="min-w-[170px] flex-1">
                <p className="font-black text-slate-700">{r.device_name}</p>
                <p className="text-xs font-semibold text-slate-400">{r.device_type}</p>
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                  Last Sync
                </p>
                <p className="mt-1 text-sm font-bold text-slate-600">
                  {r.last_sync_at ? new Date(r.last_sync_at).toLocaleString('en-PH') : 'Never'}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                  Pending
                </p>
                <p className="mt-1 text-sm font-black text-slate-700">{r.pending_transactions}</p>
              </div>
              <div className="flex items-center gap-2 rounded-full bg-slate-50 px-3 py-2 text-xs font-black text-slate-600">
                {r.sync_status === 'ONLINE' ? (
                  <CheckCircle2 size={15} className="text-emerald-600" />
                ) : r.sync_status === 'OFFLINE' ? (
                  <WifiOff size={15} className="text-amber-600" />
                ) : (
                  <XCircle size={15} className="text-red-600" />
                )}
                {r.sync_status}
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
